"use client";

import type { ActionTriggerDefinition } from "@/hooks/useActionTriggerDefinitions";
import { useActions, useLogicActions, useTriggers } from "@/hooks/useActionTriggerDefinitions";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { resolvePayloadTemplates } from "@/lib/workflow/resolvePayloadTemplates";
import { useWalletsStore } from "@/stores/walletsStore";
import type { WorkflowNodeExecutionState, WorkflowNodePayload, WorkflowRun } from "@/stores/workflowRunsStore";
import { useMemo } from "react";
import { FundsMovementBanner, getFundsMovementTitle, InlineFundsMovementSequence, resolveFundsMovementData, type LegViewModel } from "./FundsMovementBanner";
interface Props {
  run: WorkflowRun;
  isSimulation?: boolean;
}
interface NodeFundsEntry {
  nodeId: string;
  nodeType: string;
  nodeDef: ActionTriggerDefinition;
  nodeData: WorkflowNodePayload;
  nodeState: WorkflowNodeExecutionState;
  walletAddress?: string;
}
function collectFundNodes(executionState: Record<string, WorkflowNodeExecutionState> | undefined, workflowDef: Record<string, WorkflowNodePayload> | undefined, definitionMap: Map<string, ActionTriggerDefinition>, walletsMap: Map<string, {
  id: string;
  address?: string;
}>): NodeFundsEntry[] {
  if (!executionState || !workflowDef) return [];
  const entries: NodeFundsEntry[] = [];
  for (const [nodeId, nodeState] of Object.entries(executionState)) {
    const nodeData = workflowDef[nodeId];
    if (!nodeData) continue;
    const def = definitionMap.get(nodeData.type);
    if (def?.fundsMovement && nodeState.result) {
      const walletInstance = nodeData.connector?.id ? walletsMap.get(nodeData.connector.id) : undefined;
      // Resolve template variables in payload using loop context (for iteration children)
      const loopVars = (nodeState.context as Record<string, unknown> | undefined)?.loopVars as Record<string, unknown> | undefined;
      const resolvedPayload = resolvePayloadTemplates(nodeData.payload, loopVars);
      const resolvedNodeData = resolvedPayload && resolvedPayload !== nodeData.payload ? {
        ...nodeData,
        payload: resolvedPayload
      } : nodeData;
      entries.push({
        nodeId,
        nodeType: nodeData.type,
        nodeDef: def,
        nodeData: resolvedNodeData,
        nodeState,
        walletAddress: walletInstance?.address
      });
    }
    if (nodeState.iterations) {
      for (const iteration of nodeState.iterations) {
        entries.push(...collectFundNodes(iteration, workflowDef, definitionMap, walletsMap));
      }
    }
  }
  return entries;
}

/**
 * Consolidated fund movement summary for the success modal.
 * Accepts a pre-fetched WorkflowRun to avoid duplicate API calls
 * (RunDetailContent below also fetches the same run).
 */
export function RunFundsSummary({
  run,
  isSimulation
}: Props) {
  const {
    triggers
  } = useTriggers();
  const {
    actions
  } = useActions();
  const {
    logicActions
  } = useLogicActions();
  const wallets = useWalletsStore(s => s.wallets);
  const definitionMap = useMemo(() => {
    const all = [...triggers, ...actions, ...logicActions];
    return new Map(all.map(def => [def.type, def]));
  }, [triggers, actions, logicActions]);
  const entries = useMemo(() => {
    const walletsMap = new Map(wallets.map(w => [w.id, w]));
    return collectFundNodes(run.executionState, run.workflowDefinition?.nodes, definitionMap, walletsMap).sort((a, b) => {
      const aTime = a.nodeState.startedAt ? new Date(a.nodeState.startedAt).getTime() : a.nodeState.finishedAt ? new Date(a.nodeState.finishedAt).getTime() : Number.POSITIVE_INFINITY;
      const bTime = b.nodeState.startedAt ? new Date(b.nodeState.startedAt).getTime() : b.nodeState.finishedAt ? new Date(b.nodeState.finishedAt).getTime() : Number.POSITIVE_INFINITY;
      return aTime - bTime;
    });
  }, [run, definitionMap, wallets]);
  if (entries.length === 0) return null;
  const simulated = isSimulation ?? run.isSimulation ?? false;
  const anyFailed = entries.some(entry => entry.nodeState.status === "failure");
  const summaryTitle = (() => {
    const kinds = new Set(entries.map(entry_0 => entry_0.nodeDef.fundsMovement?.kind).filter(Boolean));
    if (kinds.size === 1) {
      return getFundsMovementTitle(entries[0].nodeDef.fundsMovement!.kind, simulated, anyFailed);
    }
    return anyFailed ? "Swap Attempted" : simulated ? "Would Move Funds" : "Funds Moved";
  })();
  const inlineLegs = useMemo(() => {
    // Only use inline sequence for 1-2 entries that form a true pipeline
    // (output of one feeds into the next). Independent operations (e.g., loop
    // iterations swapping different tokens) should render as separate rows.
    if (entries.length === 0 || entries.length >= 3) {
      return null;
    }
    const sequence: LegViewModel[] = [];
    for (const entry_1 of entries) {
      const movement = entry_1.nodeDef.fundsMovement;
      if (!movement) {
        return null;
      }
      const {
        sent,
        received
      } = resolveFundsMovementData(movement, entry_1.nodeData.payload, entry_1.nodeState.result, entry_1.nodeDef.resultSchema, entry_1.nodeDef.payloadSchema);
      const firstSent = sent[0];
      const firstReceived = received[0];
      if (!firstSent || !firstReceived) {
        return null;
      }
      const previousLeg = sequence[sequence.length - 1];
      if (previousLeg) {
        // Check if this entry's input is the previous entry's output (a pipeline).
        // If not, these are independent operations — don't inline them.
        const isPipeline = previousLeg.chainId === firstSent.chainId && previousLeg.nativeAsset === firstSent.nativeAsset && (previousLeg.tokenAddress ?? "").toLowerCase() === (firstSent.tokenAddress ?? "").toLowerCase() && (previousLeg.tokenLabel ?? "") === (firstSent.tokenLabel ?? "");
        if (!isPipeline) {
          return null; // Fall back to per-entry rendering
        }
      } else {
        sequence.push(firstSent);
      }
      sequence.push(firstReceived);
    }
    return sequence.length >= 2 ? sequence : null;
  }, [entries]);
  return <div className={cn("rounded-xl border p-4 pt-3", anyFailed ? "border-red-200/60 bg-red-50/30 opacity-70 dark:border-red-500/20 dark:bg-red-900/10" : simulated ? "border-purple-200/60 bg-purple-50/30 dark:border-purple-500/20 dark:bg-purple-900/10" : "border-amber-200/60 bg-amber-50/30 dark:border-amber-500/20 dark:bg-amber-900/10")}>
      <div className={cn("micro-label mb-3 flex flex-wrap items-center gap-2 font-semibold tracking-[0.18em]", anyFailed ? "text-red-600 dark:text-red-400" : simulated ? "text-purple-700 dark:text-purple-300" : "text-amber-700 dark:text-amber-300")}>
        <span>{summaryTitle}</span>
        {anyFailed && <Badge variant="destructive-red">Failed</Badge>}
      </div>

      {inlineLegs ? <InlineFundsMovementSequence legs={inlineLegs} simulated={simulated} /> : <div className={cn(entries.length > 1 && (anyFailed ? "divide-y divide-red-200/60 dark:divide-red-500/15" : simulated ? "divide-y divide-purple-200/60 dark:divide-purple-500/15" : "divide-y divide-amber-200/60 dark:divide-amber-500/15"))}>
          {entries.map((entry_2, i) => <div key={`${entry_2.nodeId}-${entry_2.nodeType}-${i}`} className={cn(i > 0 && "pt-3", i < entries.length - 1 && "pb-3")}>
              <FundsMovementBanner movement={entry_2.nodeDef.fundsMovement!} payload={entry_2.nodeData.payload} result={entry_2.nodeState.result} resultSchema={entry_2.nodeDef.resultSchema} payloadSchema={entry_2.nodeDef.payloadSchema} walletAddress={entry_2.walletAddress} isSimulation={simulated} executionStatus={entry_2.nodeState.status} showTitle={false} embedded />
            </div>)}
        </div>}
    </div>;
}